/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, Fragment } from 'react';
import axiosCall from '../services/axios';
import Style from '../css/TaxPaymentPage.module.css';
import ButtonBorder from '../components/ButtonBorder';
import ButtonBorderWrapper from '../components/ButtonBorderWrapper';
import EnvelopeWithBox from '../components/EnvelopeWithBox';
import useGetBatchList from '../common/hooks/useGetBatchesList';
import LoadingModal from '../components/modals/LoadingModal';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Autocomplete,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  InputLabel,
  FormControl,
} from '@mui/material';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useAuthContext } from '../contexts/AuthContext';
import useAbortController from '../common/hooks/useAbortController';
import useGetTaxFiles from '../common/hooks/useGetTaxFiles';

enum TaxReportOperation {
  TAX_REPORT = 'Create Tax Report',
  PAY_TAX = 'Pay Taxes',
}

enum Vendor {
  CORELOGIC = 'Corelogic',
  SLK = 'SLK',
}

function TaxPayment() {
  const [operationType, setOperationType] = useState(TaxReportOperation.TAX_REPORT as string);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [tempBatchNumber, setTempBatchNumber] = useState('');
  const { batches, batchesErrorMessages, loadingBatches } = useGetBatchList();
  const [activeBatches, setActiveBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(true);
  const [selectedFilename, setSelectedFilename] = useState('');
  const { taxFiles, taxFilesErrorMessages, loadingTaxFiles } = useGetTaxFiles(selectedVendor);
  const [date, setDate] = useState(null);

  const { token } = useAuthContext();
  const { get } = useAbortController();
  const { enqueueSnackbar } = useSnackbar();

  const handleChangeVendor = (event: SelectChangeEvent) => {
    setSelectedVendor(event.target.value);
  };

  const handleChangeFilename = (event: SelectChangeEvent) => {
    setSelectedFilename(event.target.value);
  };

  useEffect(() => {
    if (batchesErrorMessages) {
      enqueueSnackbar(batchesErrorMessages, { variant: 'error' });
    }

    if (batches) {
      setActiveBatches(batches.filter((batch) => batch.active).map((b) => b.batchNo));
    }
  }, [batches, batchesErrorMessages, enqueueSnackbar]);

  useEffect(() => {
    if (taxFilesErrorMessages) {
      enqueueSnackbar(taxFilesErrorMessages, { variant: 'error' });
    }
  }, [taxFilesErrorMessages, enqueueSnackbar]);

  const setBatchValue = (_, value: string) => {
    //this method helps us prevent against nulls in the batch number chooser
    if (value) {
      setTempBatchNumber(value);
    } else {
      setTempBatchNumber('');
    }
  };

  const handleSubmit = async () => {
    if (!token) return;

    const formData = {
      batchNo: tempBatchNumber,
      fileName: selectedFilename,
      paymentDate: date.format('MM/DD/YYYY'),
    };

    const request = { url: process.env.REACT_APP_TAX_PAYMENT + selectedVendor, data: formData, method: 'post' };
    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };
    const config = get();
    try {
      const response = await axiosCall(request, config, headers);
      enqueueSnackbar('The report will be emailed to the Tax Team when completed.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  useEffect(() => {
    setDisabled(() => {
      if (
        operationType === TaxReportOperation.PAY_TAX &&
        (tempBatchNumber === '' || selectedVendor === '' || selectedFilename === '' || date === null)
      ) {
        return true;
      } else if (
        operationType === TaxReportOperation.TAX_REPORT &&
        (selectedVendor === '' || selectedFilename === '' || date === null)
      ) {
        return true;
      } else {
        return false;
      }
    });
  }, [operationType, selectedVendor, tempBatchNumber, selectedFilename, date]);

  useEffect(() => {
    setLoading(loadingBatches || loadingTaxFiles);
  }, [loadingBatches, loadingTaxFiles]);

  return (
    <Fragment>
      <ButtonBorderWrapper>
        <EnvelopeWithBox blockName='Tax Payment'>
          {loading ? (
            <LoadingModal open={loading} />
          ) : (
            <Fragment>
              <div className={Style.horizantalDiv}>
                <span>Select Operation:</span>
                <label>
                  <input
                    type='radio'
                    name='opType'
                    checked={operationType === TaxReportOperation.TAX_REPORT}
                    onChange={() => setOperationType(TaxReportOperation.TAX_REPORT)}
                  ></input>
                  Create Tax Report
                </label>
                <label>
                  <input
                    type='radio'
                    name='opType'
                    checked={operationType === TaxReportOperation.PAY_TAX}
                    onChange={() => setOperationType(TaxReportOperation.PAY_TAX)}
                  ></input>
                  Pay Tax Report
                </label>
              </div>
              <div className={`${selectedVendor === '' ? Style.lastHorizantalDiv : Style.horizantalDiv}`}>
                <FormControl>
                  <InputLabel id='vendor-label'>Vendor</InputLabel>
                  <Select
                    labelId='vendor-label'
                    value={selectedVendor}
                    label='Vendor'
                    onChange={handleChangeVendor}
                    className={Style.select}
                  >
                    {Object.values(Vendor).map((value: string, index: number) => (
                      <MenuItem key={index} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl className={Style.datePicker}>
                    <DatePicker
                      label='Payment Date'
                      slotProps={{
                        textField: {
                          size: 'small',
                        },
                      }}
                      value={date}
                      onChange={(newValue) => setDate(newValue)}
                    />
                  </FormControl>
                </LocalizationProvider>
                {operationType === TaxReportOperation.PAY_TAX && (
                  <Autocomplete
                    options={activeBatches}
                    value={tempBatchNumber}
                    onChange={setBatchValue}
                    className={Style.textInput}
                    renderInput={(params) => <TextField {...params} />}
                  />
                )}
              </div>
              {selectedVendor !== '' && (
                <div className={Style.lastHorizantalDiv}>
                  <FormControl>
                    <InputLabel id='filename-label'>Filename</InputLabel>
                    <Select
                      labelId='filename-label'
                      value={selectedFilename}
                      label='Filename'
                      onChange={handleChangeFilename}
                      className={Style.largeSelect}
                    >
                      {taxFiles?.map((value: string, index: number) => (
                        <MenuItem key={index} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </Fragment>
          )}
        </EnvelopeWithBox>
        <ButtonBorder>
          <Button variant='contained' onClick={handleSubmit} className={Style.button} disabled={disabled}>
            Submit
          </Button>
        </ButtonBorder>
      </ButtonBorderWrapper>
    </Fragment>
  );
}

export default function TaxPaymentPage() {
  return (
    <SnackbarProvider maxSnack={3} preventDuplicate anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <TaxPayment />
    </SnackbarProvider>
  );
}
